import { fetchWithTenant } from '../utils/apiClient';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:4010/api'

// Proposal API service functions
export async function createProposal(proposalData) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals`, {
      method: 'POST',
      body: JSON.stringify(proposalData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error creating proposal:', error);
    throw error;
  }
}

export async function getUserProposals(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.source) queryParams.append('source', params.source);
    if (params.statusGroup) queryParams.append('statusGroup', params.statusGroup);
    if (params.mine !== undefined) queryParams.append('mine', params.mine ? 'true' : 'false');
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const url = `${API_BASE_URL}/proposals?${queryParams}`;

    const response = await fetchWithTenant(url, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch proposals');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching proposals:', error);
    throw error;
  }
}

export async function getProposalCounts() {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/counts`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch proposal counts');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching proposal counts:', error);
    throw error;
  }
}

export async function getProposalStatusCounts() {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/status-counts`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch proposal status counts');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching proposal status counts:', error);
    throw error;
  }
}

export async function getProposalById(proposalId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/${proposalId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching proposal:', error);
    throw error;
  }
}

export async function updateProposal(proposalId, updateData) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/${proposalId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData)
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating proposal:', error);
    throw error;
  }
}

export async function markAsReadyForReview(proposalId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/${proposalId}/ready-for-review`, {
      method: 'POST',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to mark proposal for review');
    }

    return await response.json();
  } catch (error) {
    console.error('Error marking proposal for review:', error);
    throw error;
  }
}

export async function deleteProposal(proposalId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/${proposalId}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to delete proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting proposal:', error);
    throw error;
  }
}

// Admin functions
export async function getPendingReviewProposals(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);

    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/pending-review?${queryParams}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch pending review proposals');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching pending review proposals:', error);
    throw error;
  }
}

export async function approveProposal(proposalId, comments = '') {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/${proposalId}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Tenant headers added by fetchWithTenant
      body: JSON.stringify({ comments })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to approve proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error approving proposal:', error);
    throw error;
  }
}

export async function rejectProposal(proposalId, feedback = '') {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/${proposalId}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Tenant headers added by fetchWithTenant
      body: JSON.stringify({ feedback })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reject proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error rejecting proposal:', error);
    throw error;
  }
}

export async function requestChanges(proposalId, feedback = '') {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/${proposalId}/request-changes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Tenant headers added by fetchWithTenant
      body: JSON.stringify({ feedback })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to request changes');
    }

    return await response.json();
  } catch (error) {
    console.error('Error requesting changes:', error);
    throw error;
  }
}

// Mock Upwork API functions
export async function getMockJobPosts(params = {}) {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.skills) queryParams.append('skills', params.skills);
    if (params.budgetMin) queryParams.append('budgetMin', params.budgetMin);
    if (params.budgetMax) queryParams.append('budgetMax', params.budgetMax);

    const response = await fetchWithTenant(`${API_BASE_URL}/mock/upwork/jobposts?${queryParams}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch mock job posts');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching mock job posts:', error);
    throw error;
  }
}

export async function getMockJobPostById(jobId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/mock/upwork/jobposts/${jobId}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch mock job post');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching mock job post:', error);
    throw error;
  }
}

export async function submitMockProposal(proposalId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/mock/upwork/submit-proposal`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Tenant headers added by fetchWithTenant
      body: JSON.stringify({ proposalId })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting proposal:', error);
    throw error;
  }
}

export async function getProposalStatus(proposalId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/mock/upwork/proposal-status/${proposalId}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch proposal status');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching proposal status:', error);
    throw error;
  }
}

export async function getMockClientFeedback(proposalId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/mock/upwork/client-feedback/${proposalId}`, {
      method: 'GET',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch client feedback');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching client feedback:', error);
    throw error;
  }
}

export async function submitProposal(proposalId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/${proposalId}/submit`, {
      method: 'POST',
      // Tenant headers added by fetchWithTenant
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to submit proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting proposal:', error);
    throw error;
  }
}

export async function getProposalByJobId(jobId) {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/job/${jobId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch proposal');
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching proposal by job ID:', error);
    throw error;
  }
}

export async function backgroundSyncProposals() {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/background-sync`, {
      method: 'POST',
    });

    if (!response.ok) {
      // Non-critical — don't throw, just return failure
      return { success: false };
    }

    return await response.json();
  } catch {
    // Silently fail — this is a background optimization, not a user action
    return { success: false };
  }
}

export async function syncUpworkProposals() {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/proposals/sync-upwork`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to sync proposals');
    }

    return await response.json();
  } catch (error) {
    console.error('Error syncing proposals:', error);
    throw error;
  }
}
